import courseData from "../materials/sample_questions.json";
import type { Chapter, CourseMaterial, Question } from "../types";

export type Answer = string | number | boolean;
export type AttemptStatus = "correct" | "incorrect" | "needs-more-detail";

export type Attempt = {
	answer: Answer;
	status: AttemptStatus;
	feedback?: string;
	misconceptionTags?: string[];
	supportingConcepts?: string[];
	confidence?: number;
	submittedAt: number;
};

export type ReviewQuestion = {
	id: string;
	chapterIndex: number;
	chapter: Chapter;
	question: Question;
};

export type LearnerDifficulty = {
	keyConcept: string;
	chapterTitle: string;
	outcome: Exclude<AttemptStatus, "correct">;
	misconception?: string;
	lastSeenAt: number;
};

export type ChatMessage = {
	role: "student" | "assistant";
	text: string;
};

export type ReviewSession = {
	currentIndex: number;
	mode: "all" | "missed";
	reviewChapterIds?: number[];
	attempts: Record<string, Attempt>;
	difficultyMemory: LearnerDifficulty[];
	chatByQuestion: Record<string, ChatMessage[]>;
};

export type ShortAnswerGrade = {
	outcome: AttemptStatus;
	feedback: string;
	misconceptionTags: string[];
	supportingConcepts: string[];
	confidence: number;
};

export const material = courseData as CourseMaterial;
export const sessionKey = "lilysai-guided-review-session";

export function questionAt(
	chapterIndex: number,
	questionIndex: number,
): ReviewQuestion {
	const chapter = material[chapterIndex];
	const question = chapter?.questions[questionIndex];

	if (!chapter || !question) {
		throw new Error("The demo questions could not be loaded.");
	}

	return {
		id: `chapter-${chapterIndex}-question-${questionIndex}`,
		chapterIndex,
		chapter,
		question,
	};
}

// Include every question from every chapter, preserving lesson order.
export const demoQuestions = material.flatMap((chapter, chapterIndex) =>
	chapter.questions.map((_, questionIndex) =>
		questionAt(chapterIndex, questionIndex),
	),
);

export function getInitialSession(): ReviewSession {
	if (typeof window === "undefined")
		return {
			currentIndex: 0,
			mode: "all",
			attempts: {},
			difficultyMemory: [],
			chatByQuestion: {},
		};

	try {
		const stored = window.localStorage.getItem(sessionKey);
		if (stored) {
			const session = JSON.parse(stored) as ReviewSession;
			if (
				session &&
				typeof session.currentIndex === "number" &&
				session.attempts
			)
				return {
					...session,
					difficultyMemory: session.difficultyMemory ?? [],
					chatByQuestion: session.chatByQuestion ?? {},
				};
		}
	} catch {
		window.localStorage.removeItem(sessionKey);
	}

	return {
		currentIndex: 0,
		mode: "all",
		attempts: {},
		difficultyMemory: [],
		chatByQuestion: {},
	};
}

export function isCorrect(status: AttemptStatus) {
	return status === "correct";
}

export function resultLabel(status: AttemptStatus) {
	if (status === "correct") return "Correct";
	if (status === "needs-more-detail") return "Needs a fuller answer";
	return "Not quite";
}

export function ordinal(value: number) {
	return `${value}${value === 1 ? "st" : value === 2 ? "nd" : value === 3 ? "rd" : "th"}`;
}
